import { HugeiconsIcon } from "@hugeicons/react"
import { Alert01Icon } from "@hugeicons/core-free-icons"
import { cn } from '@/lib/utils'
import type { PlanUsageCounter } from '@/lib/types/billing'

interface PlanUsageMeterProps {
  /** e.g. "Heatmaps used" */
  label: string
  counter: PlanUsageCounter
  /**
   * Optional compact label shown instead of the segmented bar when the
   * counter is unlimited (trial, fail-open, or the plan has no cap) and
   * there's existing usage worth surfacing, e.g. "3 pages". Omit to render
   * nothing in the unlimited case (the default for most surfaces).
   */
  unlimitedLabel?: string
  className?: string
}

/**
 * Segmented usage meter — "{label} {current}/{limit} · N left" plus a bar of
 * filled/empty segments. Extracted from the heatmaps page header (SUPENG-97)
 * so every plan-limited surface gets the same widget. Renders nothing while
 * the counter is unlimited (unless `unlimitedLabel` is given), which covers
 * loading/error fail-open and trial bypass for free.
 */
export function PlanUsageMeter({ label, counter, unlimitedLabel, className }: PlanUsageMeterProps) {
  if (counter.isUnlimited) {
    if (!unlimitedLabel) return null
    return (
      <span className={cn('text-xs text-muted-foreground tabular-nums whitespace-nowrap', className)}>
        {unlimitedLabel}
      </span>
    )
  }

  const { current, limit, canAdd } = counter

  return (
    <div
      className={cn(
        'flex flex-col gap-1.5 px-3.5 py-2 rounded-xl border border-border/60 bg-card',
        className,
      )}
    >
      <div className="flex items-baseline gap-2">
        <span className="text-xs font-semibold text-muted-foreground">
          {label}
        </span>
        <span className="text-[13px] font-semibold text-foreground tabular-nums">
          {current}
          <span className="text-muted-foreground font-medium"> / {limit}</span>
        </span>
        <span
          className={cn(
            'text-[11px] font-semibold',
            !canAdd ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {!canAdd ? '0 left' : `${limit - current} left`}
        </span>
      </div>
      <div className="flex gap-1">
        {Array.from({ length: Math.min(limit, 12) }).map((_, i) => (
          <span
            key={i}
            className={cn(
              'h-2 w-6 rounded-full transition-colors',
              i < current ? (!canAdd ? 'bg-destructive' : 'bg-primary') : 'bg-muted',
            )}
          />
        ))}
      </div>
    </div>
  )
}

/** Small inline alert icon used by PlanLimitBanner — exported for reuse in ad-hoc limit copy. */
export function PlanLimitIcon({
  className,
  tone = 'destructive',
}: {
  className?: string
  /** Caution (amber) for plan limits; destructive for subscription-ended. */
  tone?: 'destructive' | 'caution'
}) {
  return (
    <span
      className={cn(
        'flex shrink-0 size-[34px] items-center justify-center rounded-[9px]',
        tone === 'caution'
          ? 'bg-warning-tint text-warning'
          : 'bg-destructive/15 text-destructive',
        className,
      )}
    >
      <HugeiconsIcon icon={Alert01Icon} size={18} />
    </span>
  )
}
